import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface SectionSideNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface SectionSideNavProps {
  sections: SectionSideNavItem[];
  activeSection: string;
  onNavigate: (id: string) => void;
}

export function SectionSideNav({ sections, activeSection, onNavigate }: SectionSideNavProps) {
  const activeIndex = sections.findIndex((s) => s.id === activeSection);
  return (
    <nav
      aria-label="Section navigation"
      className="hidden lg:flex flex-col items-center fixed right-5 top-1/2 -translate-y-1/2 z-40"
    >
      <div className="relative flex flex-col items-center gap-4 py-5 px-3 rounded-full bg-[#0f2035]/80 backdrop-blur-sm border border-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
        <div className="absolute left-1/2 -translate-x-1/2 top-1 bottom-1 w-px bg-[#049fd9]/20" />
        {sections.map((section, index) => {
          const isActive = section.id === activeSection;
          const isPassed = activeIndex >= 0 && index < activeIndex;
          const Icon = section.icon;
          return (
            <div key={section.id} className="group relative flex items-center justify-center">
              <span
                className={`pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-lg bg-[#0f2035] border border-white/10 px-3 py-1.5 text-xs font-medium text-white shadow-lg flex items-center gap-1.5 transition-all duration-150 ${
                  isActive
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-focus-within:opacity-100 group-focus-within:translate-x-0'
                }`}
              >
                <Icon className="w-3.5 h-3.5 text-[#049fd9]" />
                {section.label}
              </span>
              <button
                type="button"
                aria-label={section.label}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onNavigate(section.id)}
                className={`relative z-10 rounded-full transition-all duration-200 ${
                  isActive
                    ? 'w-3.5 h-3.5 bg-[#049fd9] shadow-[0_0_0_5px_rgba(4,159,217,0.25)]'
                    : isPassed
                    ? 'w-3 h-3 bg-[#049fd9]/60 hover:bg-[#049fd9]/80'
                    : 'w-2 h-2 border border-[#049fd9]/50 bg-transparent hover:bg-[#049fd9]/20'
                }`}
              />
            </div>
          );
        })}
      </div>
    </nav>
  );
}

export default SectionSideNav;
